import { indexBy, prop, omit } from 'ramda';

import { CODE_TYPES, MANUAL_TYPES } from 'constants/LockerConstants';
import { indexByProp } from 'utils/global';
import { createReducer } from 'utils/ReduxUtils';

import * as appointmentsActionTypes from '../../../appointments/actionTypes';
import * as taskActionTypes from '../../../appointmentTask/actionTypes';
import * as planningActionTypes from '../../../planning/actionTypes';
import * as actionTypes from '../../actionTypes';

const initialState = {};

const fetchVaultsReducer = (state, { payload }) => ({
  ...state,
  ...(payload?.reduce(
    (acc, vault) => ({
      ...acc,
      [vault.id]: {
        ...(state[vault.id] ?? {}),
        ...vault,
        physicalConfig: {
          ...(state[vault.id]?.physicalConfig ?? {}),
          ...(vault.physicalConfig ?? {}),
          codeType: vault.physicalConfig?.type === MANUAL_TYPES.MANUAL ? CODE_TYPES.MANUAL : CODE_TYPES.AUTOMATIC,
        },
      },
    }),
    {}
  ) ?? {}),
});

const fetchPlanningSuccess = (state, { payload }) => {
  const lockers = payload.lockers.map(locker => ({
    ...(state?.[locker.id] ?? {}),
    ...omit(['appointments'], locker),
  }));

  return {
    ...state,
    ...indexBy(prop('id'))(lockers),
  };
};

const fetchPlanningSuccessForOneVault = (state, { payload: { locker } }) => ({
  ...state,
  [locker.id]: {
    ...(state?.[locker.id] ?? {}),
    ...omit(['appointments'], locker),
  },
});

const updateVaultReducer = (state, { payload }) => ({
  ...state,
  [payload.id]: {
    ...payload,
    ...('nextAppointmentTask' in payload ? { nextAppointmentTask: payload.nextAppointmentTask.id } : {}),
  },
});

const setVaultStatusReducer = (state, { payload }) => ({
  ...state,
  [payload.id]: {
    ...(state?.[payload.id] ?? {}),
    ...(payload.appointments?.length > 0 ? {} : { nextAppointmentTask: null }),
    status: payload.status,
  },
});

const updateFromAppointmentTask = (state, { payload }) => {
  const toUpdate = payload?.vault;

  if (!toUpdate) {
    return state;
  }

  return {
    ...state,
    [toUpdate.id]: {
      ...(state[toUpdate.id] ?? {}),
      bookingStatus: undefined,
      ...toUpdate,
    },
  };
};

const updateFromAppointment = (state, { payload }) => {
  const tasks = payload?.appointmentTasks;
  let newState = { ...state };

  if (tasks) {
    tasks.forEach(task => {
      newState = updateFromAppointmentTask(newState, { payload: task });
    });
  }

  return newState;
};

const deleteVault = (state, { payload: locker }) => {
  const lockers = Object.values(state).filter(lockerSate => lockerSate.id !== locker.id);
  return indexByProp('id', lockers);
};

const updateFromSearch = (state, { payload }) => fetchVaultsReducer(state, { payload: payload.content });

export default createReducer(initialState, {
  [actionTypes.FETCH_VAULTS_SUCCESS]: fetchVaultsReducer,
  [actionTypes.FETCH_VAULT_SUCCESS]: updateVaultReducer,
  [actionTypes.PATCH_CODE_SUCCESS]: updateVaultReducer,
  [actionTypes.DEACTIVATE_LOCKER_SUCCESS]: setVaultStatusReducer,
  [actionTypes.REACTIVATE_LOCKER_SUCCESS]: setVaultStatusReducer,
  [planningActionTypes.GET_LOCKERS_CALENDAR_SUCCESS]: fetchPlanningSuccess,
  [planningActionTypes.GET_LOCKER_CALENDAR_SUCCESS]: fetchPlanningSuccessForOneVault,
  [appointmentsActionTypes.FETCH_APPOINTMENT_SUCCESS]: updateFromAppointment,
  [taskActionTypes.POST_PREPARE_SUCCESS]: updateFromAppointmentTask,
  [taskActionTypes.POST_UNPREPARE_SUCCESS]: updateFromAppointmentTask,
  [taskActionTypes.POST_VERIFY_SUCCESS]: updateFromAppointmentTask,
  [taskActionTypes.POST_VERIFY_SUCCESS]: updateFromAppointmentTask,
  [taskActionTypes.POST_SUCCESS]: updateFromAppointmentTask,
  // Managed from dealer config
  [actionTypes.POST_LOCKER_SUCCESS]: updateVaultReducer,
  [actionTypes.PUT_LOCKER_SUCCESS]: updateVaultReducer,
  [actionTypes.DELETE_LOCKER_SUCCESS]: deleteVault,
  [actionTypes.GET_LOCKERS_SUCCESS]: updateFromSearch,
});
